import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { DimensionUnit, UnitOfMeasure } from '../../enums';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import { LocationItemEntity } from './location-item.entity';
import { WarehouseEntity } from './warehouse.entity';

export enum LocationType {
	MERCHANDISER = 'merchandiser',
	WAREHOUSE_LOCATION = 'warehouse_location',
	VEHICLE = 'vehicle',
}

export enum LocationStatus {
	ACTIVE = 'active',
	INACTIVE = 'inactive',
	MAINTENANCE = 'maintenance',
	OFFLINE = 'offline',
}

/**
 * Sub-type for warehouse locations that distinguishes pickable
 * (sellable) bins from special-purpose bins. NULL is treated as
 * `NORMAL` for backward compat — every existing location is a
 * pickable bin until tagged otherwise.
 *
 * - `NORMAL` — standard pickable bin (default; sales-pick / ATP /
 *   cart-availability queries see stock here)
 * - `QUARANTINE` — damaged-on-arrival receiving holds inventory here
 *   pending vendor-claim resolution. NOT pickable for sales orders.
 * - `STAGING` — inbound dock or pre-putaway holding bin (future use,
 *   when we split receive → putaway into two steps)
 * - `RETURNS` — customer returns holding bin. Pre-trip / post-trip
 *   inspection exceptions of reason='returned' (driver brought back
 *   units that the customer refused) TRANSFER into the per-warehouse
 *   returns bin. See `docs/claude/distribution-refactor/inspection-exceptions.md`.
 * - `MORGUE` — damaged-goods holding bin used by the pre-trip /
 *   post-trip inspection exception flow. Units found damaged on
 *   the truck TRANSFER into the per-warehouse morgue bin pending
 *   a separate write-off / RTV workflow. Distinct from QUARANTINE
 *   (which holds vendor-damaged units at receiving).
 * - `SHRINKAGE` — placeholder bin for units that were expected on
 *   the truck per the load plan but couldn't be found at inspection
 *   time. Mirrors the morgue pattern but with a separate audit
 *   trail so accounting can distinguish "physically broken" from
 *   "unaccounted for" (shrinkage). The bin doesn't hold real
 *   physical units — it's a virtual destination for accounting.
 *
 * Sellable-stock queries must filter `subType IS NULL OR subType = NORMAL`.
 * Audit and update those queries when this column is consumed —
 * tracked in the PO Receipts plan Phase 10.
 */
export enum LocationSubType {
	NORMAL = 'normal',
	QUARANTINE = 'quarantine',
	STAGING = 'staging',
	RETURNS = 'returns',
	MORGUE = 'morgue',
	SHRINKAGE = 'shrinkage',
}

@Entity('location')
export class LocationEntity extends BaseEntity {
	@Column('varchar', { name: 'description', nullable: true, length: 255 })
	public description!: string | null;

	@Column('varchar', { name: 'code', nullable: true, length: 64 })
	public code!: string | null;

	@Column('enum', {
		name: 'type',
		enum: LocationType,
		nullable: true,
	})
	public type!: LocationType | null;

	@Column('int', {
		name: 'fk_client_address__id',
		unsigned: true,
		nullable: true,
	})
	public clientAddressId!: number | null;

	@Column('int', {
		name: 'fk_warehouse__id',
		unsigned: true,
		nullable: true,
	})
	public warehouseId!: number | null;

	@Column('varchar', { name: 'locationCode', nullable: true, length: 64 })
	public locationCode!: string | null;

	@Column('int', { name: 'capacity', nullable: true })
	public capacity!: number | null;

	@Column('enum', {
		name: 'units',
		enum: UnitOfMeasure,
		nullable: true,
	})
	public units!: UnitOfMeasure | null;

	@Column('enum', {
		name: 'status',
		enum: LocationStatus,
		nullable: true,
	})
	public status!: LocationStatus | null;

	/**
	 * Pickability sub-classification for `WAREHOUSE_LOCATION` bins.
	 * NULL = NORMAL (sellable). Set to QUARANTINE for the per-warehouse
	 * damaged-receipts holding bin (one seeded per warehouse via the
	 * Phase 10 quarantine-bin migration).
	 */
	@Column('enum', {
		name: 'subType',
		enum: LocationSubType,
		nullable: true,
	})
	public subType!: LocationSubType | null;

	@Column('decimal', {
		name: 'width',
		precision: 8,
		scale: 2,
		nullable: true,
	})
	public width!: string | null;

	@Column('decimal', {
		name: 'height',
		precision: 8,
		scale: 2,
		nullable: true,
	})
	public height!: string | null;

	@Column('decimal', {
		name: 'depth',
		precision: 8,
		scale: 2,
		nullable: true,
	})
	public depth!: string | null;

	@Column('enum', {
		name: 'dimensionUnit',
		enum: DimensionUnit,
		nullable: true,
	})
	public dimensionUnit!: DimensionUnit | null;

	/**
	 * Live-position fields used for VEHICLE-type locations only.
	 *
	 * The system does NOT poll a vehicle's GPS continuously. Instead,
	 * every action the driver takes (stop arrival, stop completion,
	 * pre-trip checkout, post-trip checkin) records the device's GPS
	 * at that moment, and these fields are the cached "last known
	 * position" used by the live vehicle map. For non-vehicle
	 * locations these are always null.
	 *
	 * The map UI shows a "Updated N minutes ago" staleness pill driven
	 * by `currentLocationAt` so a stale pin can be visually
	 * distinguished from a fresh one — important since we can't tell
	 * the operator "the truck is offline" without knowing when we
	 * last heard from it.
	 */
	@Column('decimal', {
		name: 'currentLatitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLatitude!: string | null;

	@Column('decimal', {
		name: 'currentLongitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLongitude!: string | null;

	@Column('datetime', { name: 'currentLocationAt', nullable: true })
	public currentLocationAt!: Date | null;

	/**
	 * Which driver action recorded the latest position. Values:
	 *   - `stop_arrival`    — driver confirmed arrival at a stop
	 *   - `stop_complete`   — driver completed a stop
	 *   - `checkout`        — driver finished pre-trip inspection
	 *                         (leaving the yard)
	 *   - `checkin`         — driver finished post-trip inspection
	 *                         (returning to the yard)
	 *   - `manual`          — warehouse manager / admin override
	 */
	@Column('varchar', {
		name: 'currentLocationSource',
		length: 32,
		nullable: true,
	})
	public currentLocationSource!: string | null;

	@ManyToOne(() => ClientAddressEntity)
	@JoinColumn({ name: 'fk_client_address__id' })
	public fkClientAddress!: ClientAddressEntity;

	@ManyToOne(() => WarehouseEntity)
	@JoinColumn({ name: 'fk_warehouse__id' })
	public fkWarehouse!: WarehouseEntity;

	@OneToMany(
		() => LocationItemEntity,
		(locationItem) => locationItem.fkLocation,
	)
	public locationItems!: Array<LocationItemEntity>;
}
